import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import TextFilter from "components/TextFilter";
import ButtonInverted from "components/ButtonInverted";
import Pagination from "components/Pagination";
import AddUserIcon from "assets/add-user-icon.svg";
import { useGeneralProvider } from "contexts/general";
import BffService from "services/bff";
import { FIELD_MASK_OPTIONS } from "../../constants";
import AddForm from "./components/AddForm";
import EditForm from "./components/EditForm";
import DuplicateFormModal from "./components/DuplicateFormModal";
import FormsTable from "./components/FormsTable";
import {
  Container,
  ContentWrapper,
  HeaderSection,
  TitleGroup,
  PageTitle,
  PageDescription,
  FilterRow,
  FooterRow,
  FooterInfo,
} from "./styles";

function slugify(value) {
  return (value || "formulario")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "formulario";
}

function parseNumberValue(value) {
  if (value === "" || value === undefined || value === null) {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? undefined : parsedValue;
}

function getMaskType(pattern) {
  const presetOption = FIELD_MASK_OPTIONS.find(
    (option) => option.value !== "none" && option.value !== "custom" && option.pattern === pattern
  );

  if (presetOption) {
    return presetOption.value;
  }

  return pattern ? "custom" : "none";
}

function normalizeDefaultValue(field) {
  if (field.defaultValue === undefined || field.defaultValue === null) {
    return "";
  }

  if (Array.isArray(field.defaultValue)) {
    return field.defaultValue.join(", ");
  }

  return String(field.defaultValue);
}

function buildDefaultValue(field) {
  if (field.defaultValue === undefined || field.defaultValue === null || field.defaultValue === "") {
    return undefined;
  }

  if (field.type === "checkbox") {
    const checkboxValues = String(field.defaultValue)
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    return checkboxValues.length > 0 ? checkboxValues : undefined;
  }

  if (field.type === "slider") {
    return parseNumberValue(field.defaultValue);
  }

  const normalizedValue = String(field.defaultValue).trim();
  return normalizedValue || undefined;
}

function normalizeField(field) {
  return {
    id: field.id || slugify(field.label),
    type: field.type || "text",
    label: field.label || "",
    placeholder: field.placeholder || "",
    helpText: field.helpText || "",
    required: Boolean(field.validation?.required),
    options: field.options || (["checkbox", "radio", "select"].includes(field.type) ? [] : undefined),
    min: field.min ?? "",
    max: field.max ?? "",
    step: field.step ?? "",
    valueType: field.valueType || "integer",
    imageUrl: field.imageUrl || "",
    minLength: field.validation?.minLength ?? "",
    maxLength: field.validation?.maxLength ?? "",
    pattern: field.validation?.pattern || "",
    errorMessage: field.validation?.errorMessage || "",
    maskType: getMaskType(field.validation?.pattern || ""),
    defaultValue: normalizeDefaultValue(field),
  };
}

function mapFormResponseToValues(formResponse) {
  return {
    slug: formResponse.slug,
    name: formResponse.name || formResponse.structure?.title || "",
    description: formResponse.description || formResponse.structure?.description || "",
    version: formResponse.version || formResponse.structure?.version || "1.0",
    screeningType:
      formResponse.screeningType ?? formResponse.screening_type ?? "",
    isCurrent: Boolean(formResponse.isCurrent),
    steps: (formResponse.structure?.steps || []).map((step, index) => ({
      id: step.id || `etapa-${index + 1}`,
      title: step.title || "",
      description: step.description || "",
      fields: (step.fields || []).map(normalizeField),
    })),
  };
}

function validateUniqueFieldIds(formValues) {
  for (const step of formValues.steps) {
    const ids = step.fields.map((field) => field.id);
    const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);

    if (duplicates.length > 0) {
      return `Campos com ID duplicado na etapa "${step.title || step.id}": ${[
        ...new Set(duplicates),
      ].join(", ")}`;
    }
  }

  return null;
}

function validateFormStructure(formValues) {
  const summaryFields = [];
  const thermalFields = [];

  formValues.steps.forEach((step, stepIndex) => {
    step.fields.forEach((field, fieldIndex) => {
      if (field.type === "summary") {
        summaryFields.push({ stepIndex, fieldIndex });
      }

      if (field.type === "thermal_capture") {
        thermalFields.push({ stepIndex, fieldIndex });
      }
    });
  });

  if (summaryFields.length > 1) {
    return "O formulário pode ter apenas um campo de resumo.";
  }

  if (thermalFields.length > 1) {
    return "O formulário pode ter apenas um campo de captura térmica.";
  }

  if (summaryFields.length === 1) {
    const summaryField = summaryFields[0];
    const lastStepIndex = formValues.steps.length - 1;
    const lastFieldIndex = formValues.steps[lastStepIndex]?.fields.length - 1;

    if (
      summaryField.stepIndex !== lastStepIndex ||
      summaryField.fieldIndex !== lastFieldIndex
    ) {
      return "O campo de resumo precisa ser o último campo da última etapa.";
    }
  }

  return null;
}

function buildStructure(formValues) {
  return {
    id: slugify(formValues.name),
    title: formValues.name.trim(),
    description: formValues.description.trim() || undefined,
    version: formValues.version.trim(),
    steps: formValues.steps.map((step, stepIndex) => ({
      id: step.id || `etapa-${stepIndex + 1}`,
      title: step.title,
      description: step.description || undefined,
      fields: step.fields.map((field, fieldIndex) => {
        const validation = {
          ...(field.required ? { required: true } : {}),
          ...(parseNumberValue(field.minLength) !== undefined
            ? { minLength: parseNumberValue(field.minLength) }
            : {}),
          ...(parseNumberValue(field.maxLength) !== undefined
            ? { maxLength: parseNumberValue(field.maxLength) }
            : {}),
          ...(field.pattern?.trim() ? { pattern: field.pattern.trim() } : {}),
          ...(field.errorMessage?.trim()
            ? { errorMessage: field.errorMessage.trim() }
            : {}),
        };

        const baseField = {
          id: field.id || `${field.type}-${fieldIndex + 1}`,
          type: field.type,
          label: field.label,
          placeholder: field.placeholder || undefined,
          helpText: field.helpText || undefined,
          validation: Object.keys(validation).length > 0 ? validation : undefined,
          defaultValue: buildDefaultValue(field),
        };

        if (["checkbox", "radio", "select"].includes(field.type)) {
          baseField.options = (field.options || [])
            .filter((option) => option.label || option.value)
            .map((option, optionIndex) => ({
              id: `${baseField.id}-option-${optionIndex + 1}`,
              label: option.label,
              value: option.value || slugify(option.label),
            }));
        }

        if (field.type === "slider") {
          baseField.min = parseNumberValue(field.min);
          baseField.max = parseNumberValue(field.max);
          baseField.step = parseNumberValue(field.step);
          baseField.valueType = field.valueType || "integer";
        }

        if (field.type === "image_click") {
          baseField.imageUrl = field.imageUrl || undefined;
        }

        return Object.fromEntries(
          Object.entries(baseField).filter(([, value]) => value !== undefined)
        );
      }),
    })),
  };
}

function Formularios() {
  const history = useHistory();
  const {
    setLoading,
    globalUser,
    setCurrentSubtitle,
    setSelectedPage,
    isAdmin,
  } = useGeneralProvider();
  const bffService = useMemo(() => new BffService(globalUser), [globalUser]);

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("O usuário não possui acesso a esta tela. Você será redirecionado");
      history.push("/");
      return;
    }

    setSelectedPage("/formularios");
    setCurrentSubtitle("");
  }, [history, isAdmin, setCurrentSubtitle, setSelectedPage]);

  const handleLoadForms = useCallback(() => {
    setLoading(true);
    bffService
      .getForms({ page, limit: 20, search: searchText })
      .then((response) => {
        setData(response?.data?.data || []);
        setTotal(response?.data?.total || 0);
      })
      .catch(() => {
        toast.error("Falha ao carregar formulários");
        setData([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [bffService, page, searchText, setLoading]);

  useEffect(() => {
    handleLoadForms();
  }, [handleLoadForms]);

  const handleEdit = (item) => {
    setLoading(true);
    bffService
      .getForm(item.slug)
      .then((response) => {
        setSelectedItem(mapFormResponseToValues(response.data));
        setShowEdit(true);
      })
      .catch(() => toast.error("Falha ao carregar formulário"))
      .finally(() => setLoading(false));
  };

  const handleDuplicate = (item) => {
    setLoading(true);
    bffService
      .getForm(item.slug)
      .then((response) => {
        setSelectedItem(mapFormResponseToValues(response.data));
        setShowDuplicate(true);
      })
      .catch(() => toast.error("Falha ao carregar formulário"))
      .finally(() => setLoading(false));
  };

  const handleToggleStatus = (item) => {
    setLoading(true);
    bffService
      .updateFormStatus(item.slug, !item.isCurrent)
      .then(() => {
        toast.success("Situação do formulário atualizada com sucesso!");
        handleLoadForms();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Falha ao atualizar situação do formulário");
      })
      .finally(() => setLoading(false));
  };

  const closeAndReload = () => {
    setShowAdd(false);
    setShowEdit(false);
    setShowDuplicate(false);
    setSelectedItem(null);
    handleLoadForms();
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <Container>
      {showAdd ? (
        <AddForm
          setShowModal={setShowAdd}
          bffService={bffService}
          buildStructure={buildStructure}
          validateUniqueFieldIds={validateUniqueFieldIds}
          validateFormStructure={validateFormStructure}
          onSuccess={closeAndReload}
        />
      ) : null}

      {showEdit && selectedItem ? (
        <EditForm
          setShowModal={setShowEdit}
          bffService={bffService}
          formValues={selectedItem}
          buildStructure={buildStructure}
          validateUniqueFieldIds={validateUniqueFieldIds}
          validateFormStructure={validateFormStructure}
          onSuccess={closeAndReload}
        />
      ) : null}

      {showDuplicate && selectedItem ? (
        <DuplicateFormModal
          selectedItem={selectedItem}
          bffService={bffService}
          buildStructure={buildStructure}
          validateFormStructure={validateFormStructure}
          onClose={() => {
            setShowDuplicate(false);
            setSelectedItem(null);
          }}
          onSuccess={closeAndReload}
        />
      ) : null}

      <ContentWrapper>
        <HeaderSection>
          <TitleGroup>
            <PageTitle>Formulários</PageTitle>
            <PageDescription>Gerencie os formulários usados no aplicativo.</PageDescription>
          </TitleGroup>

          <ButtonInverted
            label="Adicionar Formulário"
            icon={AddUserIcon}
            onClick={() => setShowAdd(true)}
          />
        </HeaderSection>

        <FilterRow>
          <TextFilter
            searchText={searchText}
            setSearchText={(value) => {
              setPage(1);
              setSearchText(value);
            }}
            placeHolder="Busque por nome ou slug"
          />
        </FilterRow>

        <FormsTable
          data={data}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onToggleStatus={handleToggleStatus}
        />

        <FooterRow>
          <FooterInfo>{total} formulário(s) encontrado(s)</FooterInfo>
          <Pagination currentPage={page} onPageChange={setPage} totalPages={totalPages} />
        </FooterRow>
      </ContentWrapper>
    </Container>
  );
}

export default Formularios;
